import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { displayNameSchema, emailSchema, isoDateTimeSchema, localeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { newPasswordSchema, singleUseTokenSchema } from '../identity/auth.js';
export const MEMBER_ROLES = ['OWNER', 'FOUNDER', 'EXECUTIVE', 'OBSERVER'];
export const memberRoleSchema = z.enum(MEMBER_ROLES);
/** Roles that can be assigned through invitations or role changes (ownership transfer is separate). */
export const ASSIGNABLE_ROLES = ['FOUNDER', 'EXECUTIVE', 'OBSERVER'];
export const assignableRoleSchema = z.enum(ASSIGNABLE_ROLES);
export const MEMBER_STATUSES = ['ACTIVE', 'SUSPENDED', 'DEACTIVATED'];
export const memberStatusSchema = z.enum(MEMBER_STATUSES);
/** Explicit grants that extend a role (spec §4.1: "unless explicitly granted"). */
export const MEMBER_PERMISSIONS = [
    'CAST_BINDING_VOTE',
    'REASSIGN_TASKS',
    'VIEW_COMPANY_FINANCE',
    'RECORD_FINANCE_ENTRIES',
    'VIEW_AUDIT_LOG',
    'MANAGE_INTEGRATIONS',
];
export const memberPermissionSchema = z.enum(MEMBER_PERMISSIONS);
/**
 * Server-computed capabilities of the current member. The UI uses them only to hide controls;
 * every request is authorized again on the server.
 */
export const CAPABILITIES = [
    'workspace.read',
    'members.read',
    'members.manage',
    'members.invite',
    'permissions.manage',
    'founders.read',
    'founders.manage',
    'equity.read',
    'equity.manage',
    'obligations.read_own',
    'obligations.read_all',
    'obligations.manage',
    'audit.read',
    'settings.manage',
    'topics.read',
    'topics.participate',
    'topics.facilitate',
    'governance.read',
    'governance.manage',
    'decisions.read',
    'decisions.record',
    'tasks.read',
    'tasks.create',
    'tasks.reassign',
    'updates.read',
    'updates.submit',
    'updates.manage',
    'reports.read',
    'meetings.read',
    'meetings.request',
    'meetings.organize',
    'goals.read',
    'goals.manage',
    'finance.read',
    'finance.record',
    'finance.manage',
];
export const capabilitySchema = z.enum(CAPABILITIES);
export const workspaceSummarySchema = z.object({
    id: uuidSchema,
    slug: z.string(),
    name: z.string(),
    defaultTimezone: z.string(),
    defaultLocale: localeSchema,
});
export const membershipSchema = z.object({
    workspace: workspaceSummarySchema,
    member: z.object({
        id: uuidSchema,
        role: memberRoleSchema,
        status: memberStatusSchema,
        title: z.string().nullable(),
    }),
    founderProfileId: uuidSchema.nullable(),
    capabilities: z.array(capabilitySchema),
    /** True when workspace policy requires MFA for this member and it is not yet enabled. */
    mfaEnrollmentRequired: z.boolean(),
});
export const membershipListSchema = z.object({ items: z.array(membershipSchema) });
export const memberSchema = z.object({
    id: uuidSchema,
    userId: uuidSchema,
    displayName: z.string(),
    email: z.string(),
    role: memberRoleSchema,
    status: memberStatusSchema,
    title: z.string().nullable(),
    joinedAt: isoDateTimeSchema,
    deactivatedAt: isoDateTimeSchema.nullable(),
    deactivationReason: z.string().nullable(),
    founderProfileId: uuidSchema.nullable(),
    mfaEnabled: z.boolean(),
    version: versionSchema,
});
export const memberListSchema = z.object({ items: z.array(memberSchema) });
export const memberTitleSchema = z.string().trim().min(1).max(120);
export const updateMemberRequestSchema = z
    .object({
    version: versionSchema,
    title: memberTitleSchema.nullable().optional(),
    role: assignableRoleSchema.optional(),
    reason: reasonSchema.optional(),
})
    .refine((value) => value.title !== undefined || value.role !== undefined, {
    message: 'Nothing to update',
})
    .refine((value) => value.role === undefined || value.reason !== undefined, {
    message: 'A reason is required when changing a role',
    path: ['reason'],
});
export const memberStatusChangeRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const INVITATION_STATUSES = ['PENDING', 'ACCEPTED', 'REVOKED', 'EXPIRED'];
export const invitationStatusSchema = z.enum(INVITATION_STATUSES);
export const invitationSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    role: memberRoleSchema,
    title: z.string().nullable(),
    status: invitationStatusSchema,
    expiresAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
    acceptedAt: isoDateTimeSchema.nullable(),
    revokedAt: isoDateTimeSchema.nullable(),
    invitedBy: z.string(),
});
export const invitationListSchema = z.object({ items: z.array(invitationSchema) });
export const createInvitationRequestSchema = z.object({
    email: emailSchema,
    role: assignableRoleSchema,
    title: memberTitleSchema.nullable().optional(),
});
export const invitationPreviewSchema = z.object({
    workspaceName: z.string(),
    email: z.string(),
    role: memberRoleSchema,
    title: z.string().nullable(),
    invitedBy: z.string(),
    expiresAt: isoDateTimeSchema,
    /** True when the e-mail already has an account: the invitee must sign in to accept. */
    accountExists: z.boolean(),
});
export const acceptInvitationRequestSchema = z.object({
    token: singleUseTokenSchema,
    /** Required only when no account exists for the invited e-mail. */
    displayName: displayNameSchema.optional(),
    password: newPasswordSchema.optional(),
});
export const acceptInvitationResponseSchema = z.object({
    workspaceId: uuidSchema,
    memberId: uuidSchema,
    /** True when a new account was created and the caller is now signed in. */
    signedIn: z.boolean(),
});
export const permissionGrantSchema = z.object({
    id: uuidSchema,
    memberId: uuidSchema,
    memberName: z.string(),
    permission: memberPermissionSchema,
    reason: z.string(),
    decisionId: uuidSchema.nullable(),
    grantedBy: z.string(),
    grantedAt: isoDateTimeSchema,
    revokedAt: isoDateTimeSchema.nullable(),
    revokedBy: z.string().nullable(),
});
export const permissionGrantListSchema = z.object({ items: z.array(permissionGrantSchema) });
export const createPermissionGrantRequestSchema = z.object({
    memberId: uuidSchema,
    permission: memberPermissionSchema,
    reason: reasonSchema,
    decisionId: uuidSchema.nullable().optional(),
});
export const revokeRequestSchema = z.object({ reason: reasonSchema });
export const securitySettingsSchema = z.object({
    /** Spec §11: MFA "configurable as mandatory for founders" (applies to owner and founders). */
    requireMfaForFounders: z.boolean(),
    version: z.number().int().min(0),
});
export const updateSecuritySettingsRequestSchema = z.object({
    requireMfaForFounders: z.boolean(),
    /** 0 when the setting has never been saved. */
    version: z.number().int().min(0),
    reason: reasonSchema,
});
export const AUDIT_ACTOR_TYPES = ['USER', 'SERVICE_ACCOUNT', 'SYSTEM'];
export const auditEventSchema = z.object({
    id: uuidSchema,
    occurredAt: isoDateTimeSchema,
    action: z.string(),
    entityType: z.string(),
    entityId: uuidSchema.nullable(),
    actorType: z.enum(AUDIT_ACTOR_TYPES),
    actorName: z.string().nullable(),
    reason: z.string().nullable(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    requestId: z.string().nullable(),
});
export const auditEventPageSchema = cursorPageSchema(auditEventSchema);
export const auditEventQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    entityType: z
        .string()
        .regex(/^[a-z_]{1,64}$/)
        .optional(),
    entityId: uuidSchema.optional(),
});
//# sourceMappingURL=workspace.js.map